import React, { ReactNode, useState } from 'react';
import {
    Box,
    Chip,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { FieldInputProps } from 'formik';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { Icon } from '@iconify/react';
import { InputForm } from '../../types';
import { UNRESOLVED_NAME } from '../../helpers/displayName';

interface Props {
    inputs: InputForm[];
    touched: any;
    size?: 'medium' | 'small';
    values?: any;
    errors: any;
    after?: ReactNode;
    setFieldValue?(id: string, v: any): any;
    getFieldProps(n: string): FieldInputProps<any>;
}

function access(obj: any, prop: string, defVal?: any) {
    if (typeof prop === 'string') {
        var objectNames = prop.split('.');

        var curObj = obj;

        for (var i = 0, len = objectNames.length; i < len; i++) {
            curObj = curObj[objectNames[i]];
            if (typeof curObj === 'undefined') {
                return defVal;
            }
        }
        return curObj;
    }
}

const FormBuilder = (props: Props) => {
    const [showPassword, setShowPassword] = useState(false);
    const {
        inputs,
        errors,
        values,
        touched,
        setFieldValue,
        getFieldProps,
        size = 'small',
    } = props;

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    return (
        <Grid container spacing={2}>
            {inputs?.map((input, i) => {
                const error = access(errors, input?.id);
                const touch = access(touched, input?.id);

                if (input?.type === 'select') {
                    return (
                        <Grid
                            item
                            key={`input-${input?.id}-${input?.type}`}
                            {...input.grid}
                            sx={input.sx}
                        >
                            <FormControl fullWidth>
                                <InputLabel sx={{mb:1,
                                    '&.Mui-focused': {
                                        color: 'text.primary' // color when focused
                                    }}}>{input?.label}
                                </InputLabel>
                                <Select
                                    fullWidth
                                    label={input?.label}
                                    size={size}
                                    multiple={input?.isMultiple}
                                    required={input?.required}
                                    error={Boolean(error)}
                                    renderValue={input?.isMultiple ? (selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {selected.map((value: any) => {
                                            const option = input?.options?.find(opt => opt.value === value);
                                            return (
                                              <Chip
                                                key={value}
                                                // Never fall back to `value`: these are ids, and a
                                                // bare ObjectId on screen reads as corrupted data.
                                                label={
                                                  option?.label
                                                  || input?.renderChipLabel?.(value)
                                                  || UNRESOLVED_NAME
                                                }
                                                size="small"
                                              />
                                            );
                                          })}
                                        </Box>
                                      ) : undefined}
                                    {...getFieldProps(input?.id)}
                                >
                                    {input?.options?.map((op,index) => (
                                        <MenuItem key={op?.value ?? index} value={op?.value}>{op?.label}</MenuItem>
                                    ))}
                                </Select>
                                {Boolean(error) && (
                                    <FormHelperText
                                        error
                                        style={{ color: '#ff0000' }}
                                    >
                                        {error}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                    );
                }

                if (input?.type === 'date') {
                    return (
                        <Grid
                            item
                            key={`input-${input?.id}-${input?.type}`}
                            {...input.grid}
                        >
                            <Typography
                                fontSize={14}
                                mb={1}
                                fontWeight={500}
                                color="text.secondary"
                            >
                                {input?.label}
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={input?.label}
                                    onChange={(newValue) => {
                                        setFieldValue &&
                                            setFieldValue(input?.id, newValue);
                                    }}
                                    
                                    inputFormat={'dd/MM/yyyy'}
                                    value={
                                        values[input?.id]
                                            ? values[input?.id]
                                            : null
                                    }
                                    renderInput={(params) => (
                                        <TextField fullWidth {...params} />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                    );
                }

                if (input?.type === 'password') {
                    return (
                        <Grid item key={`input-${input?.id}-${input?.type}`} { ...input.grid }>
                            <Typography fontSize={14} mb={1} fontWeight={500} color="text.secondary">
                                {input?.label}
                            </Typography>
                            <TextField 
                               
                                size={size}
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                key={`${i}-${input.label}-${input.type}`}
                                placeholder={input.placeholder}
                                required={input.required}
                                error={Boolean(error)}
                                helperText={error}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleShowPassword} edge="end">
                                                <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                {...getFieldProps(input.id)}
                            />
                        </Grid>
                    )
                }

                return (
                    <Grid
                        item
                        key={`input-${input?.id}-${input?.type}`}
                        {...input.grid}
                    >
                        {input?.mask ? (
                            <>
                                <TextField
                                    variant='outlined'
                                    type={input.type}
                                    size={size}
                                    required={input?.required}
                                    fullWidth
                                    label={input?.label}
                                    key={`${i}-${input.label}-${input.type}`}
                                    placeholder={input.placeholder}
                                    error={Boolean(error)}
                                    helperText={error ? error : input?.description}
                                    {...getFieldProps(input.id)}
                                />
                            </>
                        ) : (
                            <>
                                <TextField
                                    size={size}
                                    fullWidth
                                    type={input.type}
                                    label={input?.label}
                                    key={`${i}-${input.label}-${input.type}`}
                                    placeholder={input.placeholder}
                                    required={input.required}
                                    error={Boolean(error)}
                                    helperText={error ? error : input?.description}
                                    {...getFieldProps(input.id)}
                                />
                            </>
                        )}
                    </Grid>
                );
            })}
            {props?.after}
        </Grid>
    );
};

export default FormBuilder;
